import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  collection,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query as fsQuery,
} from "firebase/firestore";
import {
  ref as rtdbRef,
  onValue,
  off as rtdbOff,
  set as rtdbSet,
  update as rtdbUpdate,
} from "firebase/database";
import {
  Loader2,
  Building2,
  Wallet,
  Play,
  AlertCircle,
  ShieldAlert,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import AdminLayout from "../../components/layouts/AdminLayout";
import { firestore, database, auth } from "../../firebaseConfig";
import toast from "react-hot-toast";
import { API_BASE_URL } from "../../utils/apiBase";
import { countSameDayPhoneDuplicates } from "../../utils/paymentDuplicateGuard";
const MAX_PHONE_LOOKUP_LIMIT =
  Number(process.env.REACT_APP_B2C_PHONE_LIMIT) || 200;
const PHONE_HISTORY_MONTHS =
  Number(process.env.REACT_APP_B2C_HISTORY_MONTHS) || 6;
const VOICE_ENABLED_DEFAULT =
  process.env.REACT_APP_PAYMENT_VOICE === "true" ? true : false;

const resolvePhoneNumber = (entry, farmersMap) => {
  if (!entry) return "";
  if (entry.PhoneNumber) return entry.PhoneNumber;
  if (entry.MSISDN) return entry.MSISDN;
  if (entry.phoneNumber) return entry.phoneNumber;

  const other = entry.OtherName || entry.otherName || entry.CloudName;
  const farmer = other && farmersMap ? farmersMap[other] : null;
  if (farmer) {
    return (
      farmer.PhoneNumber ||
      farmer.MSISDN ||
      farmer.phoneNumber ||
      farmer.phone ||
      ""
    );
  }
  return "";
};

const calcTariff = (amount, method) => {
  const m = String(method || "").toLowerCase();
  const a = Number(amount) || 0;
  if (m.includes("m-pesa")) {
    if (a <= 100) return 0;
    if (a <= 1500) return 15;
    if (a <= 5000) return 20;
    if (a <= 20000) return 23;
    if (a <= 50000) return 25;
    if (a <= 100000) return 28;
    if (a <= 250000) return 45;
    return 0;
  }
  if (m.includes("kcb")) {
    if (a <= 100) return 0;
    if (a <= 1000) return 15;
    if (a <= 2500) return 48;
    if (a <= 10000) return 48;
    if (a <= 100000) return 50;
    if (a <= 250000) return 70;
    return 0;
  }
  return 0;
};

const getPaymentKey = (payment) =>
  payment?._key ||
  payment?.Id ||
  payment?.id ||
  payment?.CloudName ||
  payment?.OtherName ||
  payment?.otherName ||
  "";

const getTransactionTime = (tx) => {
  const value = tx?.transactionDate || tx?.processedAt || tx?.completion_time;
  const date = value?.toDate ? value.toDate() : new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? 0 : time;
};

const classifyPatternWindow = (date) => {
  const dow = date.getDay();
  const dom = date.getDate();
  if ([30, 31, 1, 2].includes(dom)) return "monthly_window";
  if (dom >= 15 && dom <= 18) return "half_month_window";
  if ([4, 5].includes(dow)) return "weekly_window";
  return "other";
};

const buildFeatureVector = ({ phone, amount, ts, history }) => {
  const tsDate = new Date(ts);
  const phoneHistory = (history || [])
    .filter((h) => getTransactionTime(h) < tsDate.getTime())
    .sort((a, b) => getTransactionTime(a) - getTransactionTime(b));

  if (!phoneHistory.length) {
    return { status: "no_history" };
  }

  const numericAmounts = phoneHistory.map(
    (h) => Number(h.transactionAmount || h.amount || 0) || 0
  );
  const countPrev = numericAmounts.length;
  const sumPrev = numericAmounts.reduce((sum, v) => sum + v, 0);
  const meanPrev = sumPrev / countPrev;
  const variance =
    numericAmounts.reduce((sum, v) => sum + Math.pow(v - meanPrev, 2), 0) /
    Math.max(countPrev, 1);
  let stdPrev = Math.sqrt(variance);
  if (!Number.isFinite(stdPrev) || stdPrev === 0) {
    stdPrev = Math.sqrt(
      numericAmounts.reduce((sum, v) => sum + Math.pow(v - meanPrev, 2), 0) /
        Math.max(numericAmounts.length, 1)
    );
    if (!Number.isFinite(stdPrev) || stdPrev === 0) {
      stdPrev = 1;
    }
  }

  const dow = tsDate.getDay();
  const dom = tsDate.getDate();
  const hour = tsDate.getHours();
  const txWindow = classifyPatternWindow(tsDate);

  const patternCounts = phoneHistory.reduce((acc, h) => {
    const d = h.transactionDate || h.processedAt || h.completion_time;
    const dateObj = d?.toDate ? d.toDate() : new Date(d);
    const bucket = classifyPatternWindow(dateObj);
    acc[bucket] = (acc[bucket] || 0) + 1;
    return acc;
  }, {});
  const dominant =
    Object.keys(patternCounts).reduce((best, key) => {
      if (best === null) return key;
      return patternCounts[key] > (patternCounts[best] || 0) ? key : best;
    }, null) || "other";

  const matchesDominant = txWindow === dominant ? 1 : 0;
  const amountZ = (amount - meanPrev) / (stdPrev || 1);

  const features = {
    amount,
    day_of_week: dow,
    day_of_month: dom,
    hour,
    mean_amount_prev: meanPrev,
    std_amount_prev: stdPrev,
    amount_zscore: amountZ,
    count_prev: countPrev,
    matches_dominant_window: matchesDominant,
    tx_pattern_window_other: txWindow === "other" ? 1 : 0,
    tx_pattern_window_weekly_window: txWindow === "weekly_window" ? 1 : 0,
  };

  return {
    status: "ok",
    features,
    historyStats: {
      countPrev,
      meanPrev,
      stdPrev,
      amountZ,
      dominantWindow: dominant,
      txWindow,
    },
  };
};

const fetchPhoneHistory = async (phone) => {
  if (!firestore || !phone) return [];
  const cursor = new Date();
  const records = [];
  const windows = [];

  for (let i = 0; i < PHONE_HISTORY_MONTHS; i += 1) {
    windows.push({
      year: String(cursor.getFullYear()),
      month: String(cursor.getMonth() + 1).padStart(2, "0"),
    });
    cursor.setMonth(cursor.getMonth() - 1);
  }

  await Promise.all(
    windows.map(async ({ year, month }) => {
      const colRef = collection(
        firestore,
        "b2c_transactions_by_phone",
        phone,
        year,
        month,
        "tx"
      );
      try {
        const snap = await getDocs(
          fsQuery(
            colRef,
            orderBy("processedAt", "desc"),
            limit(MAX_PHONE_LOOKUP_LIMIT)
          )
        );
        snap.forEach((docSnap) => {
          records.push({
            id: docSnap.id,
            ...docSnap.data(),
          });
        });
      } catch (err) {
        console.error("Failed to fetch phone history", err);
      }
    })
  );

  records.sort((a, b) => getTransactionTime(b) - getTransactionTime(a));
  return records;
};

const AdminCompaniesPayments = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [balances, setBalances] = useState({});
  const [pendingPayments, setPendingPayments] = useState([]);
  const [farmersMap, setFarmersMap] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [payingId, setPayingId] = useState(null);
  const inFlightPaymentKeysRef = useRef(new Set());
  const [payAllBusy, setPayAllBusy] = useState(false);
  const [validatedFarmers, setValidatedFarmers] = useState({});
  const [voiceEnabled, setVoiceEnabled] = useState(VOICE_ENABLED_DEFAULT);
  const [reviewState, setReviewState] = useState({
    currentKey: null,
    steps: [],
    decision: "idle",
    historyStats: null,
    modelScore: null,
    flags: { history: false, model: false, duplicate: false },
    phone: "",
    payment: null,
  });

  useEffect(() => {
    if (!firestore) {
      setLoadingCompanies(false);
      return;
    }
    const q = fsQuery(
      collection(firestore, "companies"),
      orderBy("companyName", "asc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setCompanies(data);
        setSelectedCompanyId((prev) => {
          if (prev && data.some((c) => c.id === prev)) return prev;
          return data[0]?.id || null;
        });
        setLoadingCompanies(false);
      },
      () => setLoadingCompanies(false)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const balRef = rtdbRef(
      database,
      `Companies/${selectedCompanyId}/AccountBalances`
    );
    onValue(balRef, (snap) => setBalances(snap.val() || {}));
    return () => rtdbOff(balRef);
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) return;
    const paymentsRef = rtdbRef(
      database,
      `Companies/${selectedCompanyId}/NewPaymentRecord`
    );
    onValue(paymentsRef, (snap) => {
      const val = snap.val() || {};
      const list = Object.entries(val)
        .map(([key, p]) => ({ _key: key, ...p }))
        .sort((a, b) => (a.CloudName || "").localeCompare(b.CloudName || ""));
      setPendingPayments(list);
    });
    return () => rtdbOff(paymentsRef);
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId) {
      setValidatedFarmers({});
      setFarmersMap({});
      return;
    }
    const ref = rtdbRef(
      database,
      `Companies/${selectedCompanyId}/ValidatedFarmers`
    );
    const handler = (snap) => {
      const data = snap.val() || {};
      setValidatedFarmers(data);
      setFarmersMap(data);
    };
    onValue(ref, handler, () => setFarmersMap({}));
    return () => rtdbOff(ref);
  }, [selectedCompanyId]);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === selectedCompanyId) || null,
    [companies, selectedCompanyId]
  );

  const companyBankBalance = balances.company_bank?.balance || 0;

  const payableRows = useMemo(() => {
    return pendingPayments.map((p) => {
      const key = getPaymentKey(p);
      const phone = resolvePhoneNumber(p, farmersMap || validatedFarmers);
      const amount = Number(p.Amount || 0);
      const tariff = calcTariff(amount, p.Mode);
      const totalWithFees = amount + tariff;
      const canPay =
        Boolean(phone) && totalWithFees <= Number(companyBankBalance || 0);
      return {
        ...p,
        _key: key,
        phone,
        amount,
        tariff,
        totalWithFees,
        canPay,
      };
    });
  }, [pendingPayments, farmersMap, validatedFarmers, companyBankBalance]);

  const payableSummary = useMemo(() => {
    const payable = payableRows.filter((p) => p.canPay);
    const amountTotal = payable.reduce(
      (sum, p) => sum + (p.amount || 0),
      0
    );
    const totalWithFees = payable.reduce(
      (sum, p) => sum + (p.amount || 0) + (p.tariff || 0),
      0
    );
    return {
      count: payable.length,
      amountTotal,
      totalWithFees,
    };
  }, [payableRows]);

  const narrate = (text) => {
    if (!voiceEnabled) return;
    try {
      if (typeof window === "undefined") return;
      const synth = window.speechSynthesis;
      if (!synth) return;
      const utter = new SpeechSynthesisUtterance(text);
      synth.cancel();
      synth.speak(utter);
    } catch (err) {
      console.warn("Speech synthesis unavailable", err);
    }
  };

  const setReview = (payload) =>
    setReviewState((prev) => ({
      ...prev,
      ...payload,
    }));

  const analyzePayment = async (payment) => {
    if (!payment) return { decision: "blocked" };
    const phone = resolvePhoneNumber(payment, farmersMap || validatedFarmers);
    const amount = Number(payment.Amount || 0);
    const ts = new Date();
    const key = getPaymentKey(payment);

    const startingStep = [
      {
        label: "Starting review",
        detail: `Checking ${phone || "unknown"} for ${amount.toLocaleString()} KES`,
      },
    ];
    setReview({
      currentKey: key,
      steps: startingStep,
      decision: "reviewing",
      historyStats: null,
      modelScore: null,
      flags: { history: false, model: false, duplicate: false },
      phone,
      payment,
    });

    const history = await fetchPhoneHistory(phone);
    const featureResult = buildFeatureVector({
      phone,
      amount,
      ts,
      history,
    });

    const historyFlag =
      featureResult.status !== "ok" ||
      (featureResult.historyStats &&
        Math.abs(featureResult.historyStats.amountZ || 0) >= 3);

    const duplicateCount = countSameDayPhoneDuplicates({
      pendingPayments,
      currentPaymentKey: key,
      phone,
      getPaymentKey,
      resolvePhoneNumber,
      resolveContext: farmersMap || validatedFarmers,
    });
    const duplicateFlag = duplicateCount > 0;

    const nextSteps = [
      ...startingStep,
      {
        label: "History check",
        detail:
          featureResult.status === "ok"
            ? `Seen ${featureResult.historyStats.countPrev} previous payments; z-score ${(
                featureResult.historyStats.amountZ || 0
              ).toFixed(2)}`
            : "No history found for this phone",
        flag: historyFlag,
      },
      {
        label: "Duplicate phone check",
        detail: duplicateFlag
          ? `⚠️ WARNING: Phone ${phone} has ${duplicateCount + 1} payment(s) scheduled for today`
          : `No duplicate payments for ${phone} today`,
        flag: duplicateFlag,
      },
    ];

    setReview({
      currentKey: key,
      steps: nextSteps,
      decision: "reviewing",
      historyStats: featureResult.historyStats || null,
      modelScore: null,
      flags: { history: historyFlag, model: false, duplicate: duplicateFlag },
      phone,
      payment,
    });

    const user = auth?.currentUser;
    let modelFlag = false;
    let modelScore = null;
    if (user) {
      try {
        const idToken = await user.getIdToken();
        const features =
          featureResult.status === "ok"
            ? featureResult.features
            : {
                amount,
                day_of_week: ts.getDay(),
                day_of_month: ts.getDate(),
                hour: ts.getHours(),
                mean_amount_prev: featureResult.historyStats?.meanPrev ?? 0,
                std_amount_prev: featureResult.historyStats?.stdPrev ?? 1,
                amount_zscore: featureResult.historyStats?.amountZ ?? 0,
                count_prev: featureResult.historyStats?.countPrev ?? 0,
                matches_dominant_window: 0,
                tx_pattern_window_other: 1,
                tx_pattern_window_weekly_window: 0,
              };

        const response = await fetch(`${API_BASE_URL}/api/ml/score-b2c-anomaly/`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ features }),
        });

        const data = await response.json();
        if (response.ok && data?.status === "ok") {
          modelScore = Number(data.anomaly_score || 0);
          modelFlag = Boolean(data.is_anomaly);
          nextSteps.push({
            label: "Model verdict",
            detail: `IsolationForest score ${modelScore.toFixed(3)} • ${
              modelFlag ? "anomaly" : "normal"
            }`,
            flag: modelFlag,
          });
        } else {
          nextSteps.push({
            label: "Model verdict",
            detail: data?.error || "Model unavailable",
            flag: false,
          });
        }
      } catch (err) {
        console.error("Model call failed", err);
        nextSteps.push({
          label: "Model verdict",
          detail: "Unable to reach anomaly model",
          flag: false,
        });
      }
    } else {
      nextSteps.push({
        label: "Model verdict",
        detail: "Sign in to run anomaly model.",
        flag: false,
      });
    }

    const decision =
      historyFlag && modelFlag
        ? "blocked"
        : historyFlag || modelFlag || duplicateFlag
        ? "needs-approval"
        : "clear";

    const finalSteps = [
      ...nextSteps,
      {
        label: "Decision",
        detail:
          decision === "clear"
            ? "Looks safe — ready to pay"
            : decision === "blocked"
            ? "Flagged by history and model — skipping auto payment"
            : duplicateFlag
            ? "⚠️ Flagged for duplicate phone — ask admin to approve"
            : "Flagged once — ask admin to approve",
        flag: decision !== "clear",
      },
    ];

    const voiceLine =
      decision === "clear"
        ? `All checks passed for ${payment.CloudName || payment.OtherName || "farmer"}.`
        : decision === "blocked"
        ? "This payment is blocked because both checks look risky."
        : duplicateFlag
        ? `Warning: Duplicate payment detected for phone ${phone} today. This payment needs your approval.`
        : "This payment needs your approval. One of the checks flagged an issue.";

    narrate(voiceLine);

    setReview({
      currentKey: key,
      steps: finalSteps,
      decision,
      historyStats: featureResult.historyStats || null,
      modelScore,
      flags: { history: historyFlag, model: modelFlag, duplicate: duplicateFlag },
      phone,
      payment,
    });

    return { decision, flags: { history: historyFlag, model: modelFlag, duplicate: duplicateFlag } };
  };

  const triggerB2CPayment = async ({
    amount,
    phone,
    name,
    otherName,
    paymentId,
    transactionCost,
  }) => {
    const user = auth?.currentUser;
    if (!user) {
      throw new Error("You must be logged in to make payments.");
    }

    const idToken = await user.getIdToken();

    const response = await fetch(`${API_BASE_URL}/api/mpesa/b2c-payment/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        phone_number: phone,
        amount: Math.round(Number(amount || 0)),
        remarks: `Payment to ${name || otherName || ""}`,
        occasion: "Farmer Payment",
        company_name: selectedCompany?.companyName || selectedCompanyId,
        farmer_other_name: otherName || "",
        payment_id: paymentId || "",
        transaction_cost: Math.round(Number(transactionCost || 0)),
      }),
    });

    const text = await response.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      // Non‑JSON response (e.g., HTML error)
      throw new Error(
        "Unable to reach payment service. Please try again in a moment."
      );
    }

    if (!response.ok || !data.success) {
      const backendError = data?.error || data?.message || "";

      let friendly = "Payment failed. Please try again.";

      if (response.status === 401) {
        friendly = "Your session has expired. Please sign in again to continue.";
      } else if (backendError.includes("OAuth token")) {
        friendly = "Unable to contact Safaricom at the moment. Please try again later.";
      } else if (backendError.toLowerCase().includes("invalid phone")) {
        friendly = "The phone number is invalid. Use format 254XXXXXXXXX.";
      } else if (
        backendError.includes("Amount must be") ||
        backendError.includes("Invalid amount")
      ) {
        friendly = "Amount must be a positive whole number in KES.";
      } else if (
        backendError.includes("Missing required environment variables")
      ) {
        friendly =
          "Payment service is not fully configured. Please contact the administrator.";
      } else if (backendError.toLowerCase().includes("network error")) {
        friendly =
          "Network problem while contacting M‑Pesa. Please check your connection and try again.";
      }

      throw new Error(friendly);
    }

    return data.data || data;
  };

  const handlePaySingle = async (payment, override = false) => {
    if (!selectedCompanyId || !payment) return;

    const phone = resolvePhoneNumber(payment, farmersMap || validatedFarmers);
    if (!phone) {
      setStatusMessage("Cannot pay: missing phone number for this farmer.");
      return;
    }

    const amount = Number(payment.Amount || 0);
    const tariff = payment.tariff ?? calcTariff(amount, payment.Mode);
    const key = getPaymentKey(payment);
    if (inFlightPaymentKeysRef.current.has(key)) {
      return;
    }
    const displayName = payment.CloudName || payment.OtherName || "Farmer";
    const deduction = amount + tariff;

    if (deduction > Number(companyBankBalance || 0)) {
      setStatusMessage("Insufficient company bank balance for this payment.");
      return;
    }

    setPayingId(key);
    inFlightPaymentKeysRef.current.add(key);
    setStatusMessage(
      `Running fraud checks for KES ${amount.toLocaleString()} to ${displayName} on ${phone}...`
    );

    const review = override ? { decision: "clear" } : await analyzePayment(payment);
    if (review.decision === "blocked") {
      setStatusMessage(
        "Stopped: both history and model flagged this payment. Please double-check the record."
      );
      setPayingId(null);
      return;
    }

    if (review.decision === "needs-approval" && !override) {
      setStatusMessage(
        "Flagged once. Click Authorize to proceed or fix the record first."
      );
      setPayingId(null);
      return;
    }

    setStatusMessage(
      `Sending KES ${amount.toLocaleString()} to ${displayName} on ${phone}...`
    );

    try {
      const res = await triggerB2CPayment({
        amount,
        phone,
        name: payment.CloudName || payment.OtherName || "",
        otherName: payment.OtherName || payment.otherName || "",
        paymentId: key,
        transactionCost: tariff,
      });

      const desc =
        res?.ResponseDescription || res?.status || "Request sent to M‑Pesa";
      setStatusMessage(
        `Payment queued: KES ${amount.toLocaleString()} to ${displayName}. M‑Pesa response: ${desc}`
      );
      toast.success("Payment request sent to M‑Pesa");

      try {
        const newBalance = Number(companyBankBalance || 0) - deduction;
        await rtdbUpdate(
          rtdbRef(
            database,
            `Companies/${selectedCompanyId}/AccountBalances/company_bank`
          ),
          { balance: newBalance }
        );
      } catch (err) {
        console.error("Failed to update balance", err);
      }

      try {
        await rtdbSet(
          rtdbRef(
            database,
            `Companies/${selectedCompanyId}/NewPaymentRecord/${key}`
          ),
          null
        );
      } catch (err) {
        console.error("Failed to remove paid record", err);
      }
    } catch (err) {
      console.error(err);
      const friendly = err?.message || "Payment failed. Please try again.";
      setStatusMessage(`Payment error: ${friendly}`);
      toast.error(friendly);
    } finally {
      inFlightPaymentKeysRef.current.delete(key);
      setPayingId(null);
    }
  };

  const handlePayAll = async () => {
    if (!selectedCompanyId || !pendingPayments.length) return;
    setPayAllBusy(true);
    let remainingBalance = Number(companyBankBalance || 0);
    for (const p of payableRows) {
      if (!p.canPay) continue;
      const deduction = p.totalWithFees || 0;
      if (deduction > remainingBalance) {
        setStatusMessage("Stopped: insufficient balance to continue paying all.");
        break;
      }
      await handlePaySingle(p);
      remainingBalance -= deduction;
      // pause between payments to avoid hitting Safaricom B2C API rate limits
      // (Safaricom allows a limited number of B2C requests per minute)
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    setPayAllBusy(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-4">
        <section className="bg-white border border-slate-100 rounded-xl shadow-sm p-5">
          <h1 className="text-xl font-semibold text-slate-900">Companies Payments</h1>
          <p className="text-sm text-slate-600 mt-1">
            Manage bulk payments for companies.
          </p>
        </section>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-slate-900">
                Bulk Payments
              </h2>
            </div>
            {loadingCompanies && (
              <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-3 items-start">
            <div>
              <label className="text-xs text-slate-500">Select company</label>
              <select
                value={selectedCompanyId || ""}
                onChange={(e) => setSelectedCompanyId(e.target.value)}
                className="w-full border rounded px-3 py-2"
              >
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.companyName || c.id}
                  </option>
                ))}
              </select>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3">
              <Wallet className="h-5 w-5 text-emerald-600" />
              <div>
                <div className="text-xs text-slate-500">
                  Company bank balance
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  KES {Number(companyBankBalance || 0).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-3">
              <Wallet className="h-5 w-5 text-indigo-600" />
              <div>
                <div className="text-xs text-slate-500">
                  Payable farmers ({payableSummary.count})
                </div>
                <div className="text-lg font-semibold text-slate-900">
                  KES{" "}
                  {Number(payableSummary.amountTotal || 0).toLocaleString()}
                </div>
                <div className="text-[11px] text-slate-500">
                  With fees: KES{" "}
                  {Number(payableSummary.totalWithFees || 0).toLocaleString()}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-slate-500">Status</div>
              <div className="text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg p-3 h-14 overflow-hidden">
                {statusMessage || "Idle"}
              </div>
              <button
                onClick={handlePayAll}
                disabled={payAllBusy || !pendingPayments.length}
                className="inline-flex items-center gap-2 px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              >
                <Play className="h-4 w-4" />
                Pay all
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white border border-indigo-100 rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600" />
              <h3 className="text-base font-semibold text-slate-900">
                Payment analyst
              </h3>
            </div>
            <div className="text-xs text-slate-500">
              {reviewState.decision === "idle"
                ? "Waiting to review a payment"
                : reviewState.decision === "reviewing"
                ? "Analyzing..."
                : reviewState.decision === "clear"
                ? "Cleared"
                : reviewState.decision === "needs-approval"
                ? "Needs approval"
                : "Blocked"}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2 space-y-3">
              <div className="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 text-slate-700 text-xs border border-slate-200">
                  <TrendingUp className="h-4 w-4" />
                  {reviewState.phone || "No phone selected"}
                </span>
                {reviewState.historyStats && (
                  <>
                    <span className="text-xs text-slate-500">
                      Seen {reviewState.historyStats.countPrev} payments • mean{" "}
                      {Number(
                        reviewState.historyStats.meanPrev || 0
                      ).toLocaleString()}
                    </span>
                    <span className="text-xs text-slate-500">
                      z-score {(reviewState.historyStats.amountZ || 0).toFixed(2)}
                    </span>
                    <span className="text-xs text-slate-500">
                      window {reviewState.historyStats.txWindow}
                    </span>
                  </>
                )}
                {reviewState.modelScore !== null && (
                  <span className="text-xs text-slate-500">
                    model score {Number(reviewState.modelScore).toFixed(3)}
                  </span>
                )}
              </div>

              <div className="space-y-2">
                {reviewState.steps.length === 0 && (
                  <p className="text-sm text-slate-600">
                    Select a payment to see the live review trail.
                  </p>
                )}
                {reviewState.steps.map((step, idx) => (
                  <div
                    key={`${step.label}-${idx}`}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-2 ${
                      step.flag
                        ? "border-amber-200 bg-amber-50"
                        : "border-slate-200 bg-slate-50"
                    }`}
                  >
                    <ShieldAlert
                      className={`h-4 w-4 mt-0.5 ${
                        step.flag ? "text-amber-600" : "text-slate-500"
                      }`}
                    />
                    <div>
                      <div className="text-sm font-semibold text-slate-800">
                        {step.label}
                      </div>
                      <div className="text-xs text-slate-600">{step.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="text-xs text-slate-500">Action</div>
              <div className="space-y-2">
                <button
                  onClick={() => {
                    const current = payableRows.find(
                      (p) => getPaymentKey(p) === reviewState.currentKey
                    );
                    if (current) handlePaySingle(current, true);
                  }}
                  disabled={
                    reviewState.decision !== "needs-approval" ||
                    payingId === reviewState.currentKey
                  }
                  className="w-full px-3 py-2 rounded bg-indigo-600 text-white disabled:opacity-50"
                >
                  Authorize &amp; Pay
                </button>
                <button
                  onClick={() => {
                    if (!reviewState.currentKey) return;
                    setReviewState({
                      currentKey: null,
                      steps: [],
                      decision: "idle",
                      historyStats: null,
                      modelScore: null,
                      flags: { history: false, model: false, duplicate: false },
                      phone: "",
                      payment: null,
                    });
                  }}
                  className="w-full px-3 py-2 rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                >
                  Reset review
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={voiceEnabled}
                  onChange={(e) => setVoiceEnabled(e.target.checked)}
                />
                Narrate analyst updates
              </label>
              {reviewState.decision === "blocked" && (
                <p className="text-xs text-red-600">
                  Payment is blocked because both the trend and model flagged risk.
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wallet className="h-5 w-5 text-cyan-600" />
              <h3 className="font-semibold text-slate-900">
                Pending payments
              </h3>
            </div>
            <div className="text-xs text-slate-500">
              {pendingPayments.length} items
            </div>
          </div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="text-left px-4 py-2">Name</th>
                  <th className="text-left px-4 py-2">Phone</th>
                  <th className="text-left px-4 py-2">Amount</th>
                  <th className="text-left px-4 py-2">Transaction</th>
                  <th className="text-left px-4 py-2">Mode</th>
                  <th className="text-left px-4 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {payableRows.map((p) => {
                  const key = getPaymentKey(p);
                  return (
                    <tr key={key} className="border-b">
                      <td className="px-4 py-2">
                        {p.CloudName || p.OtherName || key}
                      </td>
                      <td className="px-4 py-2">{p.phone || "—"}</td>
                      <td className="px-4 py-2">
                        KES {p.amount.toLocaleString()}
                      </td>
                      <td className="px-4 py-2">
                        KES {p.tariff.toLocaleString()}
                      </td>
                      <td className="px-4 py-2">{p.Mode || "—"}</td>
                      <td className="px-4 py-2">
                        {p.canPay ? (
                          <div className="space-y-1">
                            <button
                              onClick={() => handlePaySingle(p)}
                              disabled={payingId === key}
                              className="px-3 py-1 bg-emerald-600 text-white rounded disabled:opacity-50"
                            >
                              {payingId === key
                                ? "Paying..."
                                : `Pay (fee ${p.tariff})`}
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(
                                  `⚠️ BYPASS ALL FRAUD CHECKS?\n\n` +
                                  `You are about to skip ALL security checks for this payment:\n` +
                                  `• Amount: KES ${p.amount.toLocaleString()}\n` +
                                  `• Phone: ${p.phone}\n` +
                                  `• Recipient: ${p.CloudName || p.OtherName}\n\n` +
                                  `This action will proceed without:\n` +
                                  `- History verification\n` +
                                  `- Anomaly detection\n` +
                                  `- Duplicate phone checks\n\n` +
                                  `Are you sure you want to continue?`
                                )) {
                                  handlePaySingle(p, true);
                                }
                              }}
                              disabled={payingId === key}
                              className="px-3 py-1 bg-amber-600 text-white rounded disabled:opacity-50 text-xs"
                              title="Bypass all fraud checks and pay immediately"
                            >
                              Bypass & Pay
                            </button>
                            {reviewState.currentKey === key &&
                              reviewState.decision === "needs-approval" && (
                                <button
                                  onClick={() => handlePaySingle(p, true)}
                                  aria-label={`Authorize payment of ${p.amount} to ${
                                    p.CloudName || p.OtherName || "farmer"
                                  }`}
                                  className="px-3 py-1 bg-indigo-600 text-white rounded"
                                  disabled={payingId === key}
                                >
                                  Authorize &amp; pay
                                </button>
                              )}
                            {reviewState.currentKey === key &&
                              reviewState.decision === "blocked" && (
                                <div className="space-y-1">
                                  <span className="text-xs text-red-600 block">
                                    Blocked after review
                                  </span>
                                  <button
                                    onClick={() => {
                                      if (window.confirm(
                                        `🚨 FORCE OVERRIDE BLOCKED PAYMENT?\n\n` +
                                        `This payment has been BLOCKED by multiple fraud checks!\n\n` +
                                        `Payment Details:\n` +
                                        `• Amount: KES ${p.amount.toLocaleString()}\n` +
                                        `• Phone: ${p.phone}\n` +
                                        `• Recipient: ${p.CloudName || p.OtherName}\n\n` +
                                        `BOTH history and anomaly model flagged this as suspicious.\n\n` +
                                        `This action is high risk and will be logged.\n` +
                                        `Only proceed if you have verified this payment independently.\n\n` +
                                        `Do you want to force this payment through?`
                                      )) {
                                        handlePaySingle(p, true);
                                      }
                                    }}
                                    disabled={payingId === key}
                                    className="px-3 py-1 bg-red-600 text-white rounded disabled:opacity-50 text-xs"
                                    title="Force payment despite being blocked"
                                  >
                                    Force Pay (Override)
                                  </button>
                                </div>
                              )}
                          </div>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <AlertCircle className="h-4 w-4" />
                            No phone
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!pendingPayments.length && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-500 text-sm"
                    >
                      No pending payments.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>
    </AdminLayout>
  );
};

export default AdminCompaniesPayments;
